<div
  class="search-modal"
  role="dialog"
  aria-modal="true"
  aria-labelledby="patient-search-title"
  aria-describedby="patient-search-subtitle"
  appFocusTrap
>
  <header class="search-modal__header flex align-center justify-between">
    <div class="flex align-center gap-12">
      <span class="search-modal__header-icon" aria-hidden="true">
        <app-icon
          name="person_search"
          size="sm"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
      </span>
      <div>
        <h2 id="patient-search-title" class="search-modal__title fs-16 fw-700 m-0">
          Search by Patient
        </h2>
        <!-- Both hosts render the same title, so the subtitle is the only thing telling a screen
             reader which search this is — it has to be part of the dialog's description. -->
        <p id="patient-search-subtitle" class="fs-12 m-0 search-modal__subtitle">
          {{ subtitle() }}
        </p>
      </div>
    </div>
    <button
      type="button"
      class="search-modal__close"
      (click)="close()"
      aria-label="Close patient search"
    >
      <app-icon
        name="close"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
    </button>
  </header>

  <div class="search-modal__body">
    <div class="field-grid">
      <div class="field">
        <label class="field__label fs-12 fw-600" for="patient-first-name">First Name</label>
        <input
          id="patient-first-name"
          class="field__input"
          type="text"
          [attr.maxlength]="nameMaxLength"
          [value]="firstName()"
          (input)="firstName.set($any($event.target).value)"
          (blur)="firstNameTouched.set(true)"
          (keydown.enter)="search()"
          placeholder="e.g. Sarah"
          [attr.aria-invalid]="firstNameTouched() && firstNameError() !== null"
          [attr.aria-describedby]="
            firstNameTouched() && firstNameError() ? 'patient-first-name-error' : null
          "
        />
        <!--
          Bound conditionally so aria-describedby never points at an element that isn't
          rendered. No role="alert": these fire on blur, and an assertive live region would
          interrupt the screen reader as the user tabs to the next field. The message is
          reached through the input's description instead.
        -->
        @if (firstNameTouched() && firstNameError(); as error) {
        <p id="patient-first-name-error" class="field__error fs-12 m-0">{{ error }}</p>
        }
      </div>

      <div class="field">
        <label class="field__label fs-12 fw-600" for="patient-last-name">Last Name</label>
        <input
          id="patient-last-name"
          class="field__input"
          type="text"
          [attr.maxlength]="nameMaxLength"
          [value]="lastName()"
          (input)="lastName.set($any($event.target).value)"
          (blur)="lastNameTouched.set(true)"
          (keydown.enter)="search()"
          placeholder="e.g. Thompson"
          [attr.aria-invalid]="lastNameTouched() && lastNameError() !== null"
          [attr.aria-describedby]="
            lastNameTouched() && lastNameError() ? 'patient-last-name-error' : null
          "
        />
        @if (lastNameTouched() && lastNameError(); as error) {
        <p id="patient-last-name-error" class="field__error fs-12 m-0">{{ error }}</p>
        }
      </div>

      <div class="field">
        <label class="field__label fs-12 fw-600" for="patient-dob">Date of Birth</label>
        <input
          id="patient-dob"
          class="field__input"
          type="text"
          inputmode="numeric"
          mask="00/00/0000"
          [dropSpecialCharacters]="false"
          [ngModel]="dateOfBirth()"
          (ngModelChange)="dateOfBirth.set($event)"
          (blur)="dateOfBirthTouched.set(true)"
          (keydown.enter)="search()"
          placeholder="MM/DD/YYYY"
          [attr.aria-invalid]="dateOfBirthTouched() && dateOfBirthError() !== null"
          [attr.aria-describedby]="
            dateOfBirthTouched() && dateOfBirthError() ? 'patient-dob-error' : null
          "
        />
        @if (dateOfBirthTouched() && dateOfBirthError(); as error) {
        <p id="patient-dob-error" class="field__error fs-12 m-0">{{ error }}</p>
        }
      </div>

      <div class="field">
        <label class="field__label fs-12 fw-600" for="patient-mrn">MRN</label>
        <input
          id="patient-mrn"
          class="field__input"
          type="text"
          [attr.maxlength]="medicalRecordNumberMaxLength"
          [value]="medicalRecordNumber()"
          (input)="medicalRecordNumber.set($any($event.target).value)"
          (blur)="medicalRecordNumberTouched.set(true)"
          (keydown.enter)="search()"
          placeholder="e.g. 7823011"
          [attr.aria-invalid]="medicalRecordNumberTouched() && medicalRecordNumberError() !== null"
          [attr.aria-describedby]="
            medicalRecordNumberTouched() && medicalRecordNumberError() ? 'patient-mrn-error' : null
          "
        />
        @if (medicalRecordNumberTouched() && medicalRecordNumberError(); as error) {
        <p id="patient-mrn-error" class="field__error fs-12 m-0">{{ error }}</p>
        }
      </div>
    </div>

    <div class="search-modal__actions flex justify-end align-center gap-12">
      <!--
        Standing hint rather than a blur-revealed error: it says what the search still needs while
        the Search button is disabled, so the button never reads as broken. role="status" (not
        "alert") so it is announced without interrupting the user mid-field.

        Rendered unconditionally, with the text allowed to go empty, rather than wrapped in an @if:
        a live region has to already be in the accessibility tree for a text change to be announced,
        so one created at the same moment its text appears announces nothing — which is exactly the
        case that needs announcing. `.search-modal__hint` carries `margin-right: auto`, so the empty
        node costs nothing visually.
      -->
      <p id="patient-search-hint" class="search-modal__hint fs-12 m-0" role="status">
        {{ criteriaCountError() }}
      </p>
      <button type="button" class="secondary-button fw-600" (click)="clear()">Clear</button>
      <!--
        Described by the hint above so the reason the button is off travels with it. The hint is
        also a `role="status"` live region, which covers the announcement when it appears; this
        covers a screen-reader user who reaches the button with the virtual cursor instead.
      -->
      <button
        type="button"
        class="primary-button fw-600 flex align-center gap-8"
        (click)="search()"
        [disabled]="!canSearch()"
        [attr.aria-describedby]="criteriaCountError() ? 'patient-search-hint' : null"
      >
        <app-icon
          name="search"
          size="xs"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <span>Search</span>
      </button>
    </div>
  </div>
</div>
